import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

const DISMISS_KEY = "kasahey-pwa-dismissed";

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // Zaten uygulama olarak yüklü
    if (localStorage.getItem(DISMISS_KEY)) return; // Kullanıcı kapatmış

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS için beforeinstallprompt yok — kendi ipucumuzu göster
    if (isIOS()) {
      const t = setTimeout(() => setIosHint(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      localStorage.setItem(DISMISS_KEY, "installed");
    }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setIosHint(false);
  };

  if (!visible && !iosHint) return null;

  // iOS ipucu (Safari'de "Ana Ekrana Ekle" adımını gösterir)
  if (iosHint && !visible) {
    return (
      <div className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-4 md:w-[420px] z-50 k-card p-4 shadow-xl border-l-4"
           style={{ borderLeftColor: "#D95D39" }}
           data-testid="pwa-ios-hint">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[#FFF6F1] flex items-center justify-center shrink-0">
            <Share className="h-5 w-5 text-[#D95D39]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900">Ana Ekrana Ekle</div>
            <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
              Kasa Hey'i uygulama gibi kullanmak için Safari'de <strong>Paylaş</strong> ikonuna
              (aşağıdaki kutu-yukarı ok simgesi) dokunun, ardından <strong>"Ana Ekrana Ekle"</strong>'yi seçin.
            </p>
          </div>
          <button onClick={dismiss} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Kapat">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Android/Chrome/Edge — native install prompt
  return (
    <div className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-4 md:w-[420px] z-50 k-card p-4 shadow-xl border-l-4"
         style={{ borderLeftColor: "#D95D39" }}
         data-testid="pwa-install-prompt">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-[#FFF6F1] flex items-center justify-center shrink-0">
          <Download className="h-5 w-5 text-[#D95D39]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900">Kasa Hey'i Uygulama Olarak Yükle</div>
          <p className="text-xs text-slate-600 mt-0.5">
            Ana ekrana ekleyin — telefondan hızlı erişim, offline destek, tam ekran mod.
          </p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={install}
              data-testid="pwa-install-btn"
              className="text-xs font-medium px-3 py-1.5 rounded-lg text-white"
              style={{ background: "#D95D39" }}
            >
              Yükle
            </button>
            <button onClick={dismiss} data-testid="pwa-dismiss-btn"
                    className="text-xs px-3 py-1.5 rounded-lg text-slate-600 hover:bg-[#F3F1EC]">
              Şimdi değil
            </button>
          </div>
        </div>
        <button onClick={dismiss} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Kapat">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
